import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {Camera, 
  LayoutDashboard,
  Barcode,
  GitFork,
  CheckCheck,
  Package,
  FileSpreadsheet,
  Receipt,
  FileOutput,
  RefreshCw,
  Database,
  ShieldCheck,
  Settings,
  LogOut,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Truck,
  BarChart3,
  Navigation,
  ExternalLink,
  Inbox,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Boxes,
  EyeOff,
  PackageCheck,
  RotateCcw,
  Coins,
  ArrowLeftRight,
  ShieldAlert,
  Sliders,
  ShoppingBag,
  ShoppingCart,
  PackagePlus,
  Search,
  Landmark,
  TrendingDown,
  Activity,
  Award,
  KeyRound,
  SlidersHorizontal,
  Users,
  PackageX,
  Percent,
  Globe,
  Mail,
  Share2,
} from 'lucide-react';
import { SCREENS, SCREEN_GROUPS, isPlanned, logout, me, screenFor, type Screen } from './api.js';
import { Button, Lamp } from './ui/index.js';
import { cn } from './ui/cn.js';
import { ProductLifecycleModal } from './components/ProductLifecycleModal.js';
import { VersionBadge, VersionCacheBanner } from './components/VersionIndicator.js';

const SCREEN_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  S01: LayoutDashboard,
  S02: Barcode,
  S03: GitFork,
  S07: CheckCheck,
  S04: Package,
  S06: FileSpreadsheet,
  S08: Receipt,
  S09: FileOutput,
  S10: RefreshCw,
  S05: Database,
  S11: ShieldCheck,
  S12: Settings,
  S13: LayoutDashboard,
  S14: Coins,
  S15: BarChart3,
  S16: PackageCheck,
  S17: ExternalLink,
  S19: Receipt,
  S20: Boxes,
  S21: Sparkles,
  S22: PackagePlus,
  S23: CheckCheck,
  S24: ArrowLeftRight,
  S25: Landmark,
  S26: Landmark,
  S27: TrendingDown,
  S28: Sliders,
  S29: EyeOff,
  S30: Barcode,
  S31: PackageX,
  S32: Percent,
  S33: Camera,
  S34: Globe,
  S35: ShoppingBag,
  S36: Receipt,
  S37: RotateCcw,
  S38: ArrowLeftRight,
  S39: ShieldAlert,
  S40: BarChart3,
  S41: Navigation,
  S42: Sliders,
  S43: ExternalLink,
  S45: Share2,
};

const GROUP_ACCENT: Record<string, string> = {
  Pilotage: 'bg-clay-600',
  GTIN: 'bg-herb-600',
  Catalogue: 'bg-sky-600',
  'Grossistes B2B': 'bg-amber-600',
  'Sources & diffusion': 'bg-honey-600',
  'Gestion Logistique': 'bg-emerald-600',
  Accounting: 'bg-indigo-600',
};

/** Allowed screen paths for reviewer operator role (strictly barcode review & catalog data, no system config, sage, or logistics/payments) */
const REVIEWER_ALLOWED_HREFS = new Set([
  '/workbench',
  '/barcode-checker',
  '/conflicts',
  '/matching-quality',
  '/crawled-new-products',
  '/final-ean-match',
  '/gtin-compare',
  '/products',
  '/pricing-vat',
  '/prices/competitors',
  '/price-adjustment',
  '/enrichment',
  '/image-studio',
  '/hidden-products',
  '/inventory',
  '/out-of-stock',
  '/gtin-pricing-vat',
  '/b2b-new-products',
  '/catalogue/marge-control',
  '/marge-control',
  '/catalogue/seo',
  '/seo',
]);

/** Operator subpages of the GTIN Workbench, with dedicated icons and clean routes. */
const WORKBENCH_SECTIONS = [
  { to: '/workbench', label: "File d'attente", icon: Inbox, end: true },
  { to: '/barcode-checker', label: 'Barcode Checker', icon: Barcode, end: false },
  { to: '/workbench/corrections', label: 'À corriger (Woo)', icon: AlertCircle, end: false },
  { to: '/workbench/approved', label: 'Approuvés', icon: CheckCircle2, end: false },
  { to: '/final-ean-match', label: 'Final EAN Match', icon: CheckCheck, end: false },
  { to: '/workbench/rejected', label: 'Rejetés', icon: XCircle, end: false },
  { to: '/workbench/pack', label: 'Pack', icon: Boxes, end: false },
  { to: '/hidden-products', label: 'Produits masqués', icon: EyeOff, end: false },
] as const;

/** Sections of the Settings module — the settings live in the sidebar, not
 *  in a rail inside the page (client request 2026-10-20). */
const SETTINGS_SECTIONS = [
  { to: '/settings', label: 'Général & sauvegardes', icon: SlidersHorizontal, end: true },
  { to: '/settings/portals', label: 'Portails B2B', icon: KeyRound, end: false },
  { to: '/settings/users', label: 'Utilisateurs', icon: Users, end: false },
  { to: '/settings/permissions', label: 'Permissions', icon: ShieldCheck, end: false },
  { to: '/settings/logistics', label: 'Logistique', icon: Truck, end: false },
  { to: '/settings/email', label: 'Emails transactionnels', icon: Mail, end: false },
  { to: '/settings/crawls', label: 'Crawls & crons', icon: Activity, end: false },
] as const;

/** Operator subpages of the Logistics Management module, with dedicated icons and clean routes. */
const SALES_STATS_SECTIONS = [
  { to: '/sales-stats', label: 'Par Produit', icon: ShoppingCart, end: true },
  { to: '/sales-stats/brands', label: 'Par Marque', icon: Award, end: false },
] as const;

/** Operator subpages of the CA Livraison module. */
const SHIPPING_CA_SECTIONS = [
  { to: '/shipping/ca', label: 'Synthèse CA', icon: Coins, end: true },
  { to: '/shipping/payment-slips', label: 'Bordereaux de Paiement', icon: Receipt, end: false },
] as const;

function subpageTitleFor(pathname: string): string | null {
  if (pathname === '/settings' || pathname === '/settings/general') return 'Général & Sauvegardes';
  if (pathname === '/settings/portals') return 'Identifiants Portails B2B';
  if (pathname === '/settings/users') return 'Gestion des Utilisateurs';
  if (pathname === '/settings/permissions') return 'Permissions & Rôles (RBAC)';
  if (pathname === '/settings/logistics') return 'Configuration Logistique';
  if (pathname === '/settings/email') return 'Emails Transactionnels (Mailgun / SES)';
  if (pathname === '/settings/crawls') return 'Surveillance Crawls & Crons';
  if (pathname === '/crawled-new-products') return 'Nouveautés Marché & Découverte Produits';
  if (pathname === '/enrichment') return 'Enrichissement Produits';
  if (pathname === '/image-studio') return 'Image Studio';
  if (pathname === '/sales-stats') return 'Stats Ventes';
  if (pathname === '/sales-stats/brands' || pathname === '/sales-stats/by-brand') return 'Ventes par Marque';
  if (pathname === '/hidden-products') return 'Produits masqués';
  if (pathname === '/inventory') return 'Inventaire rapide';
  if (pathname === '/out-of-stock') return 'Ruptures & disponibilité';
  if (pathname === '/gtin-pricing-vat') return 'Rapprochement Prix & TVA Grossistes';
  if (pathname === '/b2b-new-products') return 'Nouveautés Grossistes B2B';
  if (pathname === '/catalogue/marge-control' || pathname === '/marge-control') return 'Contrôle des Marges';
  if (pathname === '/catalogue/market-price-changes' || pathname === '/market-price-changes') return 'Veille Prix Marché';
  if (pathname === '/catalogue/seo' || pathname === '/seo') return 'Audit & Optimisation SEO';
  if (pathname === '/catalogue/meta-commerce' || pathname === '/meta-commerce') return 'Conformité Meta (Facebook Ads)';
  if (pathname === '/shipping/ca') return "Synthèse Chiffre d'Affaires";
  if (pathname === '/shipping/payment-slips' || pathname === '/shipping/bordereaux') return 'Bordereaux de Paiement Coursiers';
  const wbMatch = WORKBENCH_SECTIONS.find((s) => s.to === pathname);
  if (wbMatch && wbMatch.to !== '/workbench') return wbMatch.label;
  return null;
}

/** One navigation rail entry: Lucide icon, index, title, roadmap badge, with optional submenu toggle. */
function NavItem({
  screen,
  hasSubmenu = false,
  isSubmenuOpen = false,
  onToggleSubmenu,
  isAncestorActive = false,
}: {
  readonly screen: (typeof SCREENS)[number];
  readonly hasSubmenu?: boolean;
  readonly isSubmenuOpen?: boolean;
  readonly onToggleSubmenu?: () => void;
  readonly isAncestorActive?: boolean;
}): React.ReactElement {
  const planned = isPlanned(screen);
  const Icon = SCREEN_ICONS[screen.index] ?? Sparkles;

  return (
    <div className="relative group/nav">
      <NavLink
        to={screen.href}
        end={screen.href === '/' || hasSubmenu}
        onClick={() => {
          if (hasSubmenu && !isSubmenuOpen) {
            onToggleSubmenu?.();
          }
        }}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-[13px] font-medium transition-all duration-150 select-none border border-transparent',
            isActive
              ? 'bg-herb-50/90 text-herb-950 font-semibold border-herb-600/30 shadow-2xs'
              : isAncestorActive
                ? 'bg-cream/60 text-ink font-semibold border-border/70'
                : 'text-ink-soft hover:bg-cream/70 hover:text-ink hover:border-border/40',
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon
              className={cn(
                'h-4 w-4 shrink-0 transition-colors',
                isActive
                  ? 'text-herb-800'
                  : isAncestorActive
                    ? 'text-ink'
                    : 'text-ink-ghost group-hover/nav:text-ink-soft',
              )}
            />
            <span className="min-w-0 flex-1 truncate">{screen.title}</span>
            <span
              aria-hidden="true"
              className={cn(
                'font-mono text-[10px] tracking-wider shrink-0 px-1.5 py-0.5 rounded border transition-colors',
                isActive
                  ? 'bg-herb-100/90 text-herb-900 font-bold border-herb-600/30'
                  : 'text-ink-ghost bg-paper/50 border-border/40 group-hover/nav:text-ink-soft group-hover/nav:border-border/80',
              )}
            >
              {screen.index}
            </span>
            {planned && (
              <span className="text-[10px] font-medium text-ink-ghost bg-cream px-1.5 py-0.5 rounded shrink-0">
                {screen.phase}
              </span>
            )}
            {hasSubmenu && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleSubmenu?.();
                }}
                title={isSubmenuOpen ? 'Replier le sous-menu' : 'Déplier le sous-menu'}
                className="p-1 -mr-1 rounded hover:bg-cream/90 text-ink-ghost hover:text-ink transition-colors cursor-pointer"
              >
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    !isSubmenuOpen && '-rotate-90',
                  )}
                />
              </button>
            )}
          </>
        )}
      </NavLink>
    </div>
  );
}

/** Smooth collapsible subpages of the GTIN Workbench with tree guide line. */
function WorkbenchSubmenu({ isOpen }: { readonly isOpen: boolean }): React.ReactElement {
  return (
    <div
      className={cn(
        'grid transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100 mt-1 mb-1.5' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
      )}
    >
      <div className="overflow-hidden">
        <div className="ml-4 space-y-0.5 border-l-2 border-herb-600/25 pl-2.5 py-0.5">
          {WORKBENCH_SECTIONS.map((section) => {
            const SubIcon = section.icon;
            return (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all select-none',
                    isActive
                      ? 'bg-herb-100/70 text-herb-900 font-bold shadow-2xs border border-herb-600/25'
                      : 'text-ink-soft hover:bg-cream/80 hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SubIcon
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-colors',
                        isActive ? 'text-herb-700' : 'text-ink-ghost group-hover:text-ink-soft',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}



/** Smooth collapsible subpages of the Settings module. */

/** Smooth collapsible subpages of the Sales Stats module. */
function SalesStatsSubmenu({ isOpen }: { readonly isOpen: boolean }): React.ReactElement {
  return (
    <div
      className={cn(
        'grid transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100 mt-1 mb-1.5' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
      )}
    >
      <div className="overflow-hidden">
        <div className="ml-4 space-y-0.5 border-l-2 border-amber-600/25 pl-2.5 py-0.5">
          {SALES_STATS_SECTIONS.map((section) => {
            const SubIcon = section.icon;
            return (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all select-none',
                    isActive
                      ? 'bg-amber-100/70 text-amber-900 font-bold shadow-2xs border border-amber-600/25'
                      : 'text-ink-soft hover:bg-cream/80 hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SubIcon
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-colors',
                        isActive ? 'text-amber-700' : 'text-ink-ghost group-hover:text-ink-soft',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ShippingCaSubmenu({ isOpen }: { readonly isOpen: boolean }): React.ReactElement {
  return (
    <div
      className={cn(
        'grid transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100 mt-1 mb-1.5' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
      )}
    >
      <div className="overflow-hidden">
        <div className="ml-4 space-y-0.5 border-l-2 border-emerald-600/25 pl-2.5 py-0.5">
          {SHIPPING_CA_SECTIONS.map((section) => {
            const SubIcon = section.icon;
            return (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all select-none',
                    isActive
                      ? 'bg-emerald-100/70 text-emerald-900 font-bold shadow-2xs border border-emerald-600/25'
                      : 'text-ink-soft hover:bg-cream/80 hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SubIcon
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-colors',
                        isActive ? 'text-emerald-700' : 'text-ink-ghost group-hover:text-ink-soft',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SettingsSubmenu({
  isOpen,
}: {
  readonly isOpen: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'grid transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100 mt-1 mb-1.5' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
      )}
    >
      <div className="overflow-hidden">
        <div className="ml-4 space-y-0.5 border-l-2 border-sky-600/25 pl-2.5 py-0.5">
          {SETTINGS_SECTIONS.map((section) => {
            const SubIcon = section.icon;
            return (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all select-none',
                    isActive
                      ? 'bg-sky-100/70 text-sky-900 font-bold shadow-2xs border border-sky-600/25'
                      : 'text-ink-soft hover:bg-cream/80 hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SubIcon
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-colors',
                        isActive ? 'text-sky-700' : 'text-ink-ghost group-hover:text-ink-soft',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Modern SaaS Shell: clean white sidebar, breadcrumb bar, elevated stage. */
export function Layout(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me, retry: false });
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      void meQuery.refetch();
      navigate('/login');
    },
  });

  const isWorkbenchActive = location.pathname.startsWith('/workbench');
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isSalesStatsActive = location.pathname.startsWith('/sales-stats');
  const isShippingCaActive = location.pathname.startsWith('/shipping/ca') || location.pathname.startsWith('/shipping/payment-slips') || location.pathname.startsWith('/shipping/bordereaux');

  const [workbenchOpen, setWorkbenchOpen] = useState(isWorkbenchActive);
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);
  const [salesStatsOpen, setSalesStatsOpen] = useState(isSalesStatsActive);
  const [shippingCaOpen, setShippingCaOpen] = useState(isShippingCaActive);
  const [lifecycleModalOpen, setLifecycleModalOpen] = useState(false);
  const [lifecycleInitialQuery, setLifecycleInitialQuery] = useState('');
  const [sidebarFilter, setSidebarFilter] = useState('');

  // Automatically expand module submenus when navigating into them
  useEffect(() => {
    if (isWorkbenchActive) setWorkbenchOpen(true);
  }, [isWorkbenchActive]);

  useEffect(() => {
    if (isSettingsActive) setSettingsOpen(true);
    if (isSalesStatsActive) setSalesStatsOpen(true);
    if (isShippingCaActive) setShippingCaOpen(true);
  }, [isSettingsActive, isShippingCaActive]);

  // Global shortcut: Ctrl+K / Cmd+K or "/" when not typing in an input (disabled for distributor portal)
  useEffect(() => {
    if (meQuery.data?.brandScope) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setLifecycleInitialQuery('');
        setLifecycleModalOpen(true);
      } else if (
        e.key === '/' &&
        !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        setLifecycleInitialQuery('');
        setLifecycleModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [meQuery.data?.brandScope]);

  const current = screenFor(location.pathname);
  const subTitle = subpageTitleFor(location.pathname);
  const session = meQuery.data;
  const isDistributor = Boolean(session?.brandScope);

  const normalizedFilter = sidebarFilter.trim().toLowerCase();
  const filteredScreens = normalizedFilter !== ''
    ? SCREENS.filter(
        (screen) =>
          isScreenAllowed(screen) &&
          (screen.title.toLowerCase().includes(normalizedFilter) ||
            screen.index.toLowerCase().includes(normalizedFilter) ||
            screen.group.toLowerCase().includes(normalizedFilter) ||
            screen.href.toLowerCase().includes(normalizedFilter)),
      )
    : [];

  // Typed as Screen, not the SCREENS element union: /shop-manager, /suivi,
  // /carrier-report and /barcode-checker are served by screenFor but never
  // appear in the SCREENS array, so the literal union excludes them.
  const isScreenAllowed = (screen: Screen): boolean => {
    if (session?.carrierScope != null) {
      return screen.href === '/shipping' || screen.href === '/carrier-report';
    }
    if (session?.role === 'shop_manager') {
      return screen.href === '/shop-manager' || screen.href === '/woo-sync';
    }
    if (session?.role === 'supply_chain') {
      return (
        screen.href === '/gtin-pricing-vat' ||
        screen.href === '/b2b-new-products' ||
        screen.href === '/sales-stats' ||
        screen.href === '/gtin-compare' ||
        screen.href === '/crawled-new-products' ||
        screen.href === '/final-ean-match' ||
        screen.href === '/barcode-checker' ||
        screen.href === '/enrichment' ||
        screen.href === '/image-studio'
      );
    }
    if (session?.role === 'reviewer' || session?.role === 'viewer') {
      return REVIEWER_ALLOWED_HREFS.has(screen.href);
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <VersionCacheBanner />
      <div className="flex flex-1">
        {/* Modern SaaS Sidebar — completely hidden for brand distributor portal */}
        {!isDistributor && (
          <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-card lg:flex">
          {/* Brand identity */}
          <NavLink
            to={session?.role === 'reviewer' ? '/workbench' : '/'}
            className="block px-5 py-4 border-b border-border"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-herb-600 to-herb-800 text-white shadow-xs"
              >
                <span className="font-bold text-base leading-none">M</span>
              </span>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-base leading-none text-ink tracking-tight">MaPara</p>
                  <VersionBadge />
                </div>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-ink-ghost">
                  GTIN · Prix · Sage 100c
                </p>
              </div>
            </div>
          </NavLink>

          {/* Quick sidebar filter */}
          <div className="px-3 pt-3 pb-2 border-b border-border/40">
            <div className="relative">
              <input
                type="text"
                value={sidebarFilter}
                onChange={(e) => setSidebarFilter(e.target.value)}
                placeholder="Filtrer menu (S21, studio, marge)…"
                className="w-full h-8 pl-8 pr-7 text-xs bg-cream/40 border border-border/80 rounded-lg text-ink placeholder:text-ink-ghost focus:bg-card focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none transition-all"
              />
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost pointer-events-none" />
              {sidebarFilter !== '' && (
                <button
                  type="button"
                  onClick={() => setSidebarFilter('')}
                  className="absolute right-2 top-2 text-[10px] text-ink-ghost hover:text-ink font-bold p-0.5 cursor-pointer"
                  aria-label="Effacer le filtre"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

        {/* Navigation list — carrier AND shop_manager accounts see ONLY their module */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4" aria-label="Écrans de la console">
          {sidebarFilter.trim() !== '' ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between px-2.5 pb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                  Résultats ({filteredScreens.length})
                </span>
                <button
                  type="button"
                  onClick={() => setSidebarFilter('')}
                  className="text-[10px] text-herb-700 hover:underline cursor-pointer"
                >
                  Tout afficher
                </button>
              </div>
              {filteredScreens.length === 0 ? (
                <p className="px-2.5 py-4 text-xs text-ink-faint text-center">
                  Aucun écran correspondant.
                </p>
              ) : (
                <div className="space-y-0.5">
                  {filteredScreens.map((screen) => (
                    <NavItem key={screen.href} screen={screen} />
                  ))}
                </div>
              )}
            </div>
          ) : session?.carrierScope != null ? (
            <div className="space-y-1">
              <p className="eyebrow flex items-center gap-2 px-2.5 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-ghost select-none">
                <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full ring-2 ring-card shrink-0', 'bg-sky-600')} />
                Expéditions
              </p>
              <NavLink
                to="/carrier-report"
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-[13px] font-medium transition-all duration-150 select-none border border-transparent',
                    isActive
                      ? 'bg-herb-50/90 text-herb-950 font-semibold border-herb-600/30 shadow-2xs'
                      : 'text-ink-soft hover:bg-cream/70 hover:text-ink hover:border-border/40',
                  )
                }
              >
                <span className="min-w-0 flex-1 truncate">Réconciliation Colis</span>
                <span className="rounded-full bg-card border border-border px-1.5 py-0.2 text-[9px] font-bold uppercase tracking-wider text-ink-faint">
                  lecture seule
                </span>
              </NavLink>
            </div>
          ) : session?.role === 'shop_manager' ? (
            <div className="space-y-1">
              <p className="eyebrow flex items-center gap-2 px-2.5 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-ghost select-none">
                <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full ring-2 ring-card shrink-0', 'bg-sky-600')} />
                Shop Manager
              </p>
              <NavLink
                to="/shop-manager"
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-[13px] font-medium transition-all duration-150 select-none border border-transparent',
                    isActive
                      ? 'bg-herb-50/90 text-herb-950 font-semibold border-herb-600/30 shadow-2xs'
                      : 'text-ink-soft hover:bg-cream/70 hover:text-ink hover:border-border/40',
                  )
                }
              >
                <span className="min-w-0 flex-1 truncate">Shop Manager (Préparation)</span>
                <span className="rounded-full bg-card border border-border px-1.5 py-0.2 text-[9px] font-bold uppercase tracking-wider text-ink-faint">
                  SM
                </span>
              </NavLink>
            </div>
          ) : session?.role === 'supply_chain' ? (
            <div className="space-y-1">
              <p className="eyebrow flex items-center gap-2 px-2.5 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-ghost select-none">
                <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full ring-2 ring-card shrink-0', 'bg-amber-600')} />
                Grossistes B2B
              </p>
              <div className="space-y-0.5">
                {SCREENS.filter((screen) => screen.group === 'Grossistes B2B').map((screen) => (
                  <NavItem key={screen.href} screen={screen} />
                ))}
              </div>
            </div>
          ) : (
            SCREEN_GROUPS.filter((group) =>
              SCREENS.some((screen) => screen.group === group && isScreenAllowed(screen)),
            ).map((group) => {
              const groupScreens = SCREENS.filter((screen) => screen.group === group && isScreenAllowed(screen));
              return (
                <div key={group} className="space-y-1">
                  <div className="flex items-center justify-between px-2.5 pt-2 pb-0.5 select-none">
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={cn('h-1.5 w-1.5 rounded-full ring-2 ring-card shrink-0', GROUP_ACCENT[group] ?? 'bg-ink-ghost')}
                      />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                        {group}
                      </span>
                    </div>
                    <span className="font-mono text-[9px] text-ink-ghost bg-paper px-1.5 py-0.2 rounded-full border border-border/60">
                      {groupScreens.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {groupScreens.map((screen) => {
                    if (screen.href === '/workbench') {
                      return (
                        <div key={screen.href}>
                          <NavItem
                            screen={screen}
                            hasSubmenu
                            isSubmenuOpen={workbenchOpen}
                            onToggleSubmenu={() => setWorkbenchOpen((prev) => !prev)}
                            isAncestorActive={isWorkbenchActive && location.pathname !== '/workbench'}
                          />
                          <WorkbenchSubmenu isOpen={workbenchOpen} />
                        </div>
                      );
                    }

                    if (screen.href === '/settings') {
                      return (
                        <div key={screen.href}>
                          <NavItem
                            screen={screen}
                            hasSubmenu
                            isSubmenuOpen={settingsOpen}
                            onToggleSubmenu={() => setSettingsOpen((prev) => !prev)}
                            isAncestorActive={isSettingsActive && location.pathname !== '/settings'}
                          />
                          <SettingsSubmenu isOpen={settingsOpen} />
                        </div>
                      );
                    }



                    if (screen.href === '/sales-stats') {
                      return (
                        <div key={screen.href}>
                          <NavItem
                            screen={screen}
                            hasSubmenu
                            isSubmenuOpen={salesStatsOpen}
                            onToggleSubmenu={() => setSalesStatsOpen((prev) => !prev)}
                            isAncestorActive={isSalesStatsActive && location.pathname !== '/sales-stats'}
                          />
                          <SalesStatsSubmenu isOpen={salesStatsOpen} />
                        </div>
                      );
                    }

                    if (screen.href === '/shipping/ca') {
                      return (
                        <div key={screen.href}>
                          <NavItem
                            screen={screen}
                            hasSubmenu
                            isSubmenuOpen={shippingCaOpen}
                            onToggleSubmenu={() => setShippingCaOpen((prev) => !prev)}
                            isAncestorActive={isShippingCaActive && location.pathname !== '/shipping/ca'}
                          />
                          <ShippingCaSubmenu isOpen={shippingCaOpen} />
                        </div>
                      );
                    }

                    return (
                      <div key={screen.href}>
                        <NavItem screen={screen} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }))}
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-border bg-cream/30 p-3.5">
          <div className="flex items-center justify-between">
            <Lamp tone="good" pulse label="Pipeline opérationnel" />
            <span className="font-mono text-[10px] font-semibold text-ink-ghost">prod ready</span>
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-ink-faint">
            Approbation humaine requise avant synchronisation.
          </p>
        </div>
      </aside>
      )}

      {/* Main Content Area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sticky Glassmorphic Header */}
        <header className="sticky top-0 z-30 border-b border-border bg-card/85 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-8">
            {/* Breadcrumbs with Subpage Awareness / Distributor Portal Title */}
            {isDistributor ? (
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-herb-600 to-herb-800 text-white shadow-xs"
                  >
                    <span className="font-bold text-sm leading-none">M</span>
                  </span>
                  <div>
                    <span className="font-bold text-sm text-ink tracking-tight">MaPara Intelligence</span>
                    <span className="hidden sm:inline-block ml-2 text-xs font-semibold text-ink-ghost">
                      · Portail Distributeur
                    </span>
                  </div>
                </div>
                {session?.brandScope && (
                  <span className="hidden md:inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-0.5 text-[11px] font-bold text-sky-800">
                    Marques : {session.brandScope}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 min-w-0 text-xs sm:text-[13px] text-ink-soft">
                <span className="font-semibold text-ink">MaPara</span>
                {current !== undefined && (
                  <>
                    <ChevronRight className="h-3.5 w-3.5 text-ink-ghost" />
                    <span className="text-ink-faint">{current.group}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-ink-ghost" />
                    <span
                      className={cn(
                        'truncate',
                        subTitle ? 'text-ink-soft font-normal' : 'font-semibold text-ink',
                      )}
                    >
                      {current.title}
                    </span>
                    {subTitle && (
                      <>
                        <ChevronRight className="h-3.5 w-3.5 text-ink-ghost" />
                        <span className="font-semibold text-ink truncate">{subTitle}</span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Quick Omnisearch Trigger (hidden for brand distributors) */}
            {!isDistributor && (
              <div className="flex-1 max-w-sm mx-3 hidden sm:block">
                <button
                  type="button"
                  onClick={() => {
                    setLifecycleInitialQuery('');
                    setLifecycleModalOpen(true);
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-ink-muted bg-cream/40 hover:bg-cream border border-border rounded-xl shadow-2xs hover:border-herb-500/40 transition-all text-left"
                  title="Rechercher un produit et voir son cycle de vie complet (Ctrl+K)"
                >
                  <div className="flex items-center gap-2 truncate">
                    <Search className="w-3.5 h-3.5 text-ink-ghost shrink-0" />
                    <span className="truncate">Rechercher un produit (SKU, EAN)...</span>
                  </div>
                  <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold text-ink-faint bg-white border border-border rounded shrink-0 shadow-2xs">
                    Ctrl K
                  </kbd>
                </button>
              </div>
            )}

            {/* User Session & Logout */}
            <div className="flex items-center gap-2">
              {!isDistributor && (
                <button
                  type="button"
                  onClick={() => {
                    setLifecycleInitialQuery('');
                    setLifecycleModalOpen(true);
                  }}
                  className="p-1.5 rounded-lg text-ink-muted hover:text-ink hover:bg-cream border border-border sm:hidden transition-colors"
                  title="Rechercher un produit (Ctrl+K)"
                >
                  <Search className="w-4 h-4" />
                </button>
              )}
              <VersionBadge compact />
              {session !== undefined && (
                <>
                  <div className="flex items-center gap-2 rounded-full border border-border bg-cream/60 px-3 py-1 shadow-xs">
                    <span
                      aria-hidden="true"
                      className="grid h-5 w-5 place-items-center rounded-full bg-herb-600 text-[10px] font-bold text-white uppercase"
                    >
                      {session.email.slice(0, 1)}
                    </span>
                    <span className="max-w-[10rem] sm:max-w-[14rem] truncate text-xs font-medium text-ink">
                      {session.email}
                    </span>
                    <span className="rounded-full bg-card border border-border px-1.5 py-0.2 text-[9px] font-bold uppercase tracking-wider text-ink-faint">
                      {isDistributor ? 'Distributeur · Lecture seule' : session.role}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="soft"
                    size="xs"
                    disabled={logoutMutation.isPending}
                    onClick={() => logoutMutation.mutate()}
                    icon={<LogOut className="h-3.5 w-3.5" />}
                  >
                    <span className="hidden sm:inline">
                      {logoutMutation.isPending ? 'Sortie…' : 'Déconnexion'}
                    </span>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Mobile responsive navigation pill bar (hidden for brand distributors) */}
          {!isDistributor && (
            <nav
              aria-label="Écrans de la console (mobile)"
              className="flex gap-1.5 overflow-x-auto border-t border-border px-3 py-2 lg:hidden bg-card/60"
            >
              {SCREENS.filter((screen) => isScreenAllowed(screen)).map((screen) => (
                <NavLink
                  key={screen.href}
                  to={screen.href}
                  end={screen.href === '/'}
                  className={({ isActive }) =>
                    cn(
                      'shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-all',
                      isActive
                        ? 'border-herb-600/30 bg-herb-50 text-herb-800 font-semibold'
                        : 'border-border bg-card text-ink-soft hover:bg-cream',
                    )
                  }
                >
                  {screen.title}
                </NavLink>
              ))}
            </nav>
          )}
        </header>

        {/* Page Content Stage */}
        <main className="flex-1 px-4 py-6 sm:px-8">
          <div key={location.pathname} className="mx-auto w-full max-w-[1400px] animate-rise">
            <Outlet />
          </div>
        </main>

        {/* Global Footer */}
        <footer className="border-t border-border bg-card/40 px-4 py-3 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-faint">
            {isDistributor ? (
              <p>
                MaPara Intelligence · Surveillance Tarifaire & Marché
              </p>
            ) : (
              <p>
                MaPara · Console de contrôle catalogue & matching — {SCREENS.length} écrans
              </p>
            )}
            <div className="flex items-center gap-2 font-mono text-[11px]">
              <span>Traçabilité horodatée · audit immutable</span>
              <span>·</span>
              <span className="text-ink-muted">v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.2.0'} ({typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'dev'})</span>
            </div>
          </div>
        </footer>
      </div>
    </div>

      {!isDistributor && (
        <ProductLifecycleModal
          isOpen={lifecycleModalOpen}
          onClose={() => setLifecycleModalOpen(false)}
          initialQuery={lifecycleInitialQuery}
        />
      )}
    </div>
  );
}
